import type { Region, HistoricalEvent, Era, Dynasty, Figure } from '../types'
import { formatYear, formatYearRange, normalizeQuery } from '../utils/format'
import { nameIndex } from '../utils/nameIndex'
import { hcipUrl } from '../data/hcipSources'

interface Props {
  region: Region | null
  era: Era | null
  year: number | null
  figure: Figure | null
  events: HistoricalEvent[]                  // already filtered by App
  regionsById: Map<string, Region>
  dynastiesById: Map<string, Dynasty>
  figuresById: Map<string, Figure>
  onSelectRegion: (regionId: string) => void
  onSelectFigure: (figureId: string) => void
  onClearRegion: () => void                  // broadens the view: clears region, keeps time
  onClearTime: () => void                    // broadens the view: clears era/year, keeps region
  onClearFigure: () => void                  // broadens the view: clears figure
}

export default function DetailPanel(props: Props) {
  const { region, era, year, figure, events, regionsById, dynastiesById, figuresById,
          onSelectRegion, onSelectFigure, onClearRegion, onClearTime, onClearFigure } = props
  const hasTime = year !== null || era !== null
  const hasRegion = region !== null
  const hasFigure = figure !== null

  // Phrase the current time selection naturally for inline use. Era gets its
  // title; a specific year gets formatted; otherwise we say "this period".
  const timePhrase = year !== null
    ? formatYear(year)
    : era
      ? `the ${era.title}`
      : 'this period'

  return (
    <aside className="absolute inset-0 overflow-y-auto pl-1 pr-2">
      <div className="space-y-4 pb-12">

        {/* Empty state */}
        {!hasRegion && !hasTime && !hasFigure && (
          <EmptyState />
        )}

        {/* Figure header */}
        {figure && (
          <header className="space-y-1">
            <div className="flex items-baseline justify-between gap-2">
              <div className="text-[10px] uppercase tracking-[0.18em] text-ink-50">Person</div>
              <button
                onClick={onClearFigure}
                className="text-[10px] text-saffron-400 hover:text-saffron-500"
                title="Stop filtering by this person"
              >clear</button>
            </div>
            <h2 className="font-display text-2xl text-ink-300 iast leading-tight">
              {figure.nameIAST ?? figure.name}
            </h2>
            <p className="text-xs text-ink-50">
              {figure.role}
              {(figure.born !== undefined || figure.died !== undefined) && (
                <span> · {figure.born !== undefined ? formatYear(figure.born) : '?'} – {figure.died !== undefined ? formatYear(figure.died) : '?'}</span>
              )}
            </p>
            {figure.summary && (
              <p className="text-sm text-ink-100 mt-2 leading-relaxed">{figure.summary}</p>
            )}
            {/* Associated regions as clickable pills — quick navigation to
                each place the figure is tied to. */}
            {figure.associatedRegionIds && figure.associatedRegionIds.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1.5">
                {figure.associatedRegionIds.map(rid => {
                  const r = regionsById.get(rid)
                  if (!r) return null
                  return (
                    <button
                      key={rid}
                      onClick={() => onSelectRegion(rid)}
                      className="pill pill-ink pill-link iast"
                    >{r.primaryNameIAST ?? r.primaryName}</button>
                  )
                })}
              </div>
            )}
          </header>
        )}

        {/* Region header */}
        {region && (
          <header className="space-y-1">
            <div className="text-[10px] uppercase tracking-[0.18em] text-ink-50">Place</div>
            <h2 className="font-display text-2xl text-ink-300 iast leading-tight">
              {region.primaryNameIAST ?? region.primaryName}
              {region.greaterIndia && (
                <span className="ml-2 align-middle text-[10px] uppercase tracking-wider text-indigo_deep-300">limb</span>
              )}
            </h2>
            <p className="text-xs text-ink-50">{region.modernEquivalent.join(' · ')}</p>
            {region.blurb && (
              <p className="text-sm text-ink-100 mt-2 leading-relaxed">{region.blurb}</p>
            )}
          </header>
        )}

        {/* Time header */}
        {(era || year !== null) && (
          <div className="border-t border-ink-300/10 pt-3 space-y-1">
            <div className="text-[10px] uppercase tracking-[0.18em] text-ink-50">Time</div>
            <h3 className="font-display text-lg text-ink-300 iast">
              {year !== null
                ? formatYear(year)
                : era
                  ? `${era.title} — ${formatYearRange(era.startYear, era.endYear)}`
                  : ''}
            </h3>
            {era && year === null && (
              <p className="text-sm text-ink-100 leading-relaxed">{era.blurb}</p>
            )}
          </div>
        )}

        {/* Event list */}
        <div className="border-t border-ink-300/10 pt-3">
          <div className="flex items-baseline justify-between">
            <h4 className="text-[10px] uppercase tracking-[0.18em] text-ink-50">
              {/* The figure context, if set, is already in the figure header;
                  this label describes the SLICE of the figure's life shown. */}
              {hasFigure && hasRegion && hasTime ? 'At this place, in this period' :
               hasFigure && hasRegion            ? 'At this place, across the canon' :
               hasFigure && hasTime              ? 'In this period' :
               hasFigure                          ? 'Across the canon' :
               hasRegion && hasTime               ? 'At this place, at this time' :
               hasRegion                          ? 'Across time at this place'  :
                                                    'Across India at this moment'}
            </h4>
            <span className="text-[10px] text-ink-50">{events.length} {events.length === 1 ? 'event' : 'events'}</span>
          </div>

          {/* Empty state — figure-aware. Three flavours of "0 events":
              1) Figure + time/region selected → name the missed intersection
                 in the figure's life, offer to widen along the other axis.
              2) Region + time selected (no figure) → Bhavan-doesn't-engage
                 message with two broaden buttons.
              3) Anything else → soft "current curation" fallback. */}
          {events.length === 0 && hasFigure && figure && (hasTime || hasRegion) && (
            <div className="mt-3 space-y-3">
              <p className="text-sm text-ink-100 italic leading-relaxed">
                {hasRegion && hasTime && region
                  ? `Bhavan does not place ${figure.nameIAST ?? figure.name} at ${region.primaryNameIAST ?? region.primaryName} during ${timePhrase}.`
                  : hasRegion && region
                    ? `Bhavan does not place ${figure.nameIAST ?? figure.name} at ${region.primaryNameIAST ?? region.primaryName}.`
                    : `Bhavan does not place ${figure.nameIAST ?? figure.name} in ${timePhrase}.`}
                {' '}Broaden the view:
              </p>
              <div className="flex flex-col gap-1.5 pt-1">
                {hasTime && (
                  <button
                    onClick={onClearTime}
                    className="text-left text-xs px-3 py-2 rounded bg-parchment-200 hover:bg-saffron-50 hover:text-saffron-500 text-ink-100 border border-ink-300/5 transition-colors iast flex items-baseline justify-between gap-2"
                  >
                    <span>{figure.nameIAST ?? figure.name} across the canon</span>
                    <span aria-hidden="true">→</span>
                  </button>
                )}
                {hasRegion && region && (
                  <button
                    onClick={onClearRegion}
                    className="text-left text-xs px-3 py-2 rounded bg-parchment-200 hover:bg-saffron-50 hover:text-saffron-500 text-ink-100 border border-ink-300/5 transition-colors iast flex items-baseline justify-between gap-2"
                  >
                    <span>{figure.nameIAST ?? figure.name} away from {region.primaryNameIAST ?? region.primaryName}</span>
                    <span aria-hidden="true">→</span>
                  </button>
                )}
                <button
                  onClick={onClearFigure}
                  className="text-left text-xs px-3 py-2 rounded bg-parchment-200 hover:bg-saffron-50 hover:text-saffron-500 text-ink-100 border border-ink-300/5 transition-colors iast flex items-baseline justify-between gap-2"
                >
                  <span>All events {hasTime ? `in ${timePhrase}` : hasRegion && region ? `at ${region.primaryNameIAST ?? region.primaryName}` : 'across the canon'}</span>
                  <span aria-hidden="true">→</span>
                </button>
              </div>
            </div>
          )}

          {events.length === 0 && !hasFigure && hasRegion && hasTime && region && (
            <div className="mt-3 space-y-3">
              <p className="text-sm text-ink-100 italic leading-relaxed">
                Bhavan does not engage {region.primaryNameIAST ?? region.primaryName} structurally during {timePhrase}.
                The current curation has nothing at this intersection — broaden along either axis:
              </p>
              <div className="flex flex-col gap-1.5 pt-1">
                <button
                  onClick={onClearRegion}
                  className="text-left text-xs px-3 py-2 rounded bg-parchment-200 hover:bg-saffron-50 hover:text-saffron-500 text-ink-100 border border-ink-300/5 transition-colors iast flex items-baseline justify-between gap-2"
                >
                  <span>Across India during {timePhrase}</span>
                  <span aria-hidden="true">→</span>
                </button>
                <button
                  onClick={onClearTime}
                  className="text-left text-xs px-3 py-2 rounded bg-parchment-200 hover:bg-saffron-50 hover:text-saffron-500 text-ink-100 border border-ink-300/5 transition-colors iast flex items-baseline justify-between gap-2"
                >
                  <span>{region.primaryNameIAST ?? region.primaryName} across all eras</span>
                  <span aria-hidden="true">→</span>
                </button>
              </div>
            </div>
          )}

          {events.length === 0 && !hasFigure && !(hasRegion && hasTime) && (
            <p className="text-sm text-ink-50/80 italic mt-3">
              Nothing in the current curation yet. As we deepen the dataset from the OCR'd volumes (Phase 2),
              this gap will fill in.
            </p>
          )}

          <div className="mt-3 space-y-3">
            {events.map(ev => (
              <EventCard
                key={ev.id}
                ev={ev}
                regionsById={regionsById}
                dynastiesById={dynastiesById}
                figuresById={figuresById}
                onSelectRegion={onSelectRegion}
                onSelectFigure={onSelectFigure}
              />
            ))}
          </div>
        </div>
      </div>
    </aside>
  )
}

// -----------------------------------------------------------------------------

// If a Sanskrit term happens to also be a known entity name (e.g. "Kurukṣetra"
// is in Kuru's aliases), surface that link so the pill becomes navigable while
// still showing its gloss on hover. We require an exact normalized match —
// substring hits would produce too many false positives.
function resolveTermLink(term: string): { kind: 'region' | 'figure'; id: string } | null {
  const q = normalizeQuery(term)
  if (!q) return null
  for (const e of nameIndex) {
    if (e.q === q) {
      if (e.kind === 'figure' && e.figureId) return { kind: 'figure', id: e.figureId }
      if (e.regionId)                          return { kind: 'region', id: e.regionId }
    }
  }
  return null
}

function EventCard({
  ev,
  regionsById,
  dynastiesById,
  figuresById,
  onSelectRegion,
  onSelectFigure
}: {
  ev: HistoricalEvent
  regionsById: Map<string, Region>
  dynastiesById: Map<string, Dynasty>
  figuresById: Map<string, Figure>
  onSelectRegion: (regionId: string) => void
  onSelectFigure: (figureId: string) => void
}) {
  const dynasty = ev.dynastyId ? dynastiesById.get(ev.dynastyId) : undefined
  const figures = (ev.figureIds ?? []).map(id => figuresById.get(id)).filter(Boolean) as Figure[]
  const otherRegions = ev.regionIds
    .map(id => regionsById.get(id))
    .filter(Boolean) as Region[]

  return (
    <article className="p-3 rounded-md bg-parchment-50 border border-ink-300/8 shadow-card">
      <div className="flex items-baseline justify-between gap-2">
        <h5 className="font-display text-base text-ink-300 iast leading-snug">{ev.title}</h5>
        <span className="text-[11px] text-ink-50 whitespace-nowrap font-mono">
          {formatYearRange(ev.year, ev.yearEnd, ev.yearCirca)}
        </span>
      </div>

      <p className="text-sm text-ink-100 mt-1.5 leading-relaxed">{ev.summary}</p>

      {ev.bhavanCommentary && (
        <p className="text-[13px] font-display italic text-ink-200 mt-2 leading-relaxed border-l-2 border-saffron-200 pl-3 py-0.5">
          {ev.bhavanCommentary}
        </p>
      )}

      {(ev.sanskritTerms?.length ?? 0) > 0 && (
        <div className="mt-2 flex flex-wrap gap-1.5">
          {ev.sanskritTerms!.map(t => {
            // If this term is also a place or person (Kurukṣetra → Kuru,
            // Pāṭaliputra → Magadha, etc.) the pill navigates (pill-link).
            // Otherwise it just carries a glossary tooltip (pill-info).
            const link = resolveTermLink(t.term)
            if (!link) {
              return (
                <span key={t.term} className="pill pill-indigo pill-info iast" title={t.gloss}>
                  {t.term}
                </span>
              )
            }
            return (
              <button
                key={t.term}
                className="pill pill-indigo pill-link iast"
                title={t.gloss}
                onClick={() => link.kind === 'region' ? onSelectRegion(link.id) : onSelectFigure(link.id)}
              >{t.term}</button>
            )
          })}
        </div>
      )}

      {(otherRegions.length > 0 || dynasty || figures.length > 0) && (
        <div className="mt-2.5 flex flex-wrap gap-1.5 text-[11px]">
          {dynasty && (
            <span
              className="pill pill-saffron pill-info iast"
              title={`${dynasty.name} (${formatYearRange(dynasty.startYear, dynasty.endYear)}) — ${dynasty.summary}`}
            >{dynasty.nameIAST ?? dynasty.name}</span>
          )}
          {figures.map(f => (
            <button
              key={f.id}
              onClick={() => onSelectFigure(f.id)}
              title={f.role}
              className="pill pill-teal pill-link iast"
            >{f.nameIAST ?? f.name}</button>
          ))}
          {otherRegions.map(r => (
            <button
              key={r.id}
              onClick={() => onSelectRegion(r.id)}
              className="pill pill-ink pill-link iast"
            >{r.primaryNameIAST ?? r.primaryName}</button>
          ))}
        </div>
      )}

      {ev.sourceRefs.length > 0 && (
        <div className="mt-2.5 pt-2 border-t border-ink-300/8 text-[11px] text-ink-50 font-body space-y-0.5">
          {ev.sourceRefs.map((ref, i) => {
            const url = hcipUrl(ref.volume, ref.pages)
            const label = (
              <>
                <em className="italic">HCIP</em> Vol {ref.volume}
                {ref.chapter && <>, Ch {ref.chapter}</>}
                {ref.section && <> {ref.section}</>}
                {ref.pages && <>, pp. {ref.pages}</>}
              </>
            )
            return (
              <div key={i}>
                {url ? (
                  <a
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:text-saffron-400 underline decoration-ink-300/20 underline-offset-2 hover:decoration-saffron-400 transition-colors"
                    title={`Open Vol ${ref.volume} on archive.org`}
                  >
                    {label}
                    <span aria-hidden="true" className="ml-1 text-[10px] opacity-60">↗</span>
                  </a>
                ) : (
                  label
                )}
              </div>
            )
          })}
        </div>
      )}
    </article>
  )
}

function EmptyState() {
  return (
    <div className="space-y-4 text-sm text-ink-100 leading-relaxed">
      <h2 className="font-display text-2xl text-ink-300 iast">Welcome to the Atlas.</h2>
      <p>
        Pick a place — by modern name (<em className="italic">Patna</em>, <em className="italic">Hampi</em>,
        <em className="italic"> Pune</em>) or its historical one (<em className="italic">Pāṭaliputra</em>,
        <em className="italic"> Vijayanagara</em>, <em className="italic"> Puṇya-nagarī</em>). Or pick a year
        on the ribbon above. Or pick both.
      </p>
      <p className="text-ink-50">
        The narrative voice throughout follows the framing of R. C. Majumdar's eleven-volume
        <em className="italic"> History and Culture of the Indian People</em> (Bharatiya Vidya Bhavan).
        Sanskrit terms appear in pills — hover to see their glosses.
      </p>
      <div className="grid grid-cols-2 gap-2 pt-2">
        {[
          { y: -322, label: 'Maurya founding' },
          { y: 320,  label: 'Gupta restoration' },
          { y: 1010, label: 'Bṛhadīśvara at Tanjore' },
          { y: 1526, label: 'Bābur at Pānipat' },
          { y: 1674, label: 'Śivājī crowned' },
          { y: 1947, label: 'Independence' }
        ].map(s => (
          <button
            key={s.y}
            onClick={() => {
              const ev = new CustomEvent('atlas:set-year', { detail: s.y })
              window.dispatchEvent(ev)
            }}
            className="text-left text-xs px-3 py-2 rounded bg-parchment-200 hover:bg-saffron-50 hover:text-saffron-500 text-ink-100 border border-ink-300/5 transition-colors iast"
          >
            <span className="block text-[10px] text-ink-50">{s.y < 0 ? Math.abs(s.y) + ' BCE' : s.y + ' CE'}</span>
            <span>{s.label}</span>
          </button>
        ))}
      </div>
    </div>
  )
}
